import { useEffect, useRef } from 'react'

/**
 * Aplica a classe `is-visible` a todos os elementos com [data-fade]
 * dentro do container retornado, quando entram no viewport.
 *
 * Uso no JSX:
 *   <section ref={sectionRef}>
 *     <h2 data-fade>Título</h2>
 *     <p data-fade data-fade-delay="150">Subtítulo</p>
 *   </section>
 *
 * Atributos opcionais no elemento:
 *   data-fade-delay="150"   → atraso extra em ms
 *
 * @param duration  Duração da transição em ms (padrão: 1500)
 */
export function useFadeUp<T extends HTMLElement = HTMLElement>(duration = 1500) {
  const ref = useRef<T | null>(null)

  useEffect(() => {
    const container = ref.current
    if (!container) return

    const targets = container.querySelectorAll<HTMLElement>('[data-fade]')

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const el = entry.target as HTMLElement
          const delay = el.dataset.fadeDelay ?? '0'
          el.style.transitionDuration = `${duration}ms`
          setTimeout(() => el.classList.add('is-visible'), Number(delay))
          observer.unobserve(el)
        })
      },
      { threshold: 0.12 }
    )

    targets.forEach((t) => observer.observe(t))
    return () => observer.disconnect()
  }, [duration])

  return ref
}